import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { PaginationControls } from '@/components/pagination-controls';
import { DEFAULT_PAGE_SIZE, type PaginationMetadata } from '@/types/pagination';
import { fetchEmployeesPaged } from '@/lib/data-service';
import type { Employee } from '@/types';

const SEARCH_DEBOUNCE_MS = 500;

interface EmployeeComboboxProps {
  employees: Employee[];
  value?: string;
  onValueChange: (value: string, employee: Employee | undefined) => void;
  placeholder?: string;
  /** Heading of the "Show More" dialog, e.g. "Select Account Manager". */
  title?: string;
  recentEmployeeIds?: string[];
  maxRecentEmployees?: number;
  /** Restricts the "Show More" table to employees with access to the selected company. */
  companyAccessOnly?: boolean;
}

export function EmployeeCombobox({
  employees,
  value,
  onValueChange,
  placeholder = 'Select a project manager...',
  title = 'Select Project Manager',
  recentEmployeeIds = [],
  maxRecentEmployees = 5,
  companyAccessOnly = false,
}: EmployeeComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [popoverSearch, setPopoverSearch] = React.useState('');

  // "Show More" table state. The table holds one server-returned page, never the whole list.
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);
  const [pagedEmployees, setPagedEmployees] = React.useState<Employee[]>([]);
  const [pagination, setPagination] = React.useState<PaginationMetadata | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const requestIdRef = React.useRef(0);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // A new search or page size always starts at the first page.
  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  React.useEffect(() => {
    if (!modalOpen) return;

    // Only the newest request may write state; a slower earlier page must not overwrite it.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    fetchEmployeesPaged({ page, pageSize, search: debouncedSearch, companyAccessOnly })
      .then((response) => {
        if (requestIdRef.current !== requestId) return;
        setPagedEmployees(response.data);
        setPagination(response.pagination);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        console.error('Failed to load employees:', err);
        setError('Failed to load employees. Please try again.');
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false);
      });
  }, [modalOpen, page, pageSize, debouncedSearch, companyAccessOnly]);

  // Get recent employees
  const recentEmployees = React.useMemo(() => {
    return recentEmployeeIds
      .map((id) => employees.find((e) => String(e.id) === id))
      .filter((e): e is Employee => e !== undefined)
      .slice(0, maxRecentEmployees);
  }, [employees, recentEmployeeIds, maxRecentEmployees]);

  // Filter all employees for popover search
  const popoverFilteredEmployees = React.useMemo(() => {
    if (!popoverSearch.trim()) return [];
    const query = popoverSearch.toLowerCase();
    return employees.filter(
      (employee) =>
        employee.name.toLowerCase().includes(query) ||
        employee.role?.toLowerCase().includes(query) ||
        employee.email?.toLowerCase().includes(query) ||
        employee.department?.toLowerCase().includes(query) ||
        employee.externalUserId?.toLowerCase().includes(query)
    );
  }, [employees, popoverSearch]);

  // Get selected employee
  const selectedEmployee = employees.find((employee) => String(employee.id) === value);

  const handleSelect = (employee: Employee) => {
    onValueChange(String(employee.id), employee);
    setOpen(false);
    setModalOpen(false);
  };

  const handleShowMore = () => {
    setOpen(false);
    setModalOpen(true);
    setSearchQuery('');
    setDebouncedSearch('');
    setPage(1);
  };

  return (
    <>
      {/* Main Combobox - Shows Recent Employees */}
      <Popover open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setPopoverSearch(''); }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedEmployee ? selectedEmployee.name : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search employees..."
              value={popoverSearch}
              onValueChange={setPopoverSearch}
            />
            <CommandList>
              {popoverSearch.trim() ? (
                <>
                  <CommandEmpty>No employees found.</CommandEmpty>
                  <CommandGroup heading="Search Results">
                    {popoverFilteredEmployees.map((employee) => (
                      <CommandItem
                        key={employee.id}
                        value={String(employee.id)}
                        onSelect={() => handleSelect(employee)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value === String(employee.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <Avatar className="mr-2 h-8 w-8 rounded-md">
                          <AvatarImage src={employee.photo} alt={employee.name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                            {getInitials(employee.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="font-medium">{employee.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {employee.role}
                            {employee.department && ` - ${employee.department}`}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              ) : (
                <>
                  <CommandEmpty>No recent employees found.</CommandEmpty>
                  {/* Recent Employees */}
                  {recentEmployees.length > 0 && (
                    <CommandGroup heading="Recent Employees">
                      {recentEmployees.map((employee) => (
                        <CommandItem
                          key={employee.id}
                          value={String(employee.id)}
                          onSelect={() => handleSelect(employee)}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              value === String(employee.id) ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <Avatar className="mr-2 h-8 w-8 rounded-md">
                            <AvatarImage src={employee.photo} alt={employee.name} />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                              {getInitials(employee.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="font-medium">{employee.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {employee.role}
                              {employee.department && ` - ${employee.department}`}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {/* Show More Button */}
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleShowMore}
                      className="cursor-pointer justify-center font-medium text-primary"
                    >
                      <ChevronsUpDown className="mr-2 h-4 w-4" />
                      Show More ({employees.length - recentEmployees.length} more employees)
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Modal Dialog - Shows All Employees in Table */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Choose a person from the list below. Click on a row to select.
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, role, email, department, external ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Table Container with Scroll */}
          <div className="flex-1 overflow-auto border rounded-md">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead className="w-[56px]"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>External ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-destructive py-8">
                      {error}
                    </TableCell>
                  </TableRow>
                ) : loading && pagedEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Loading employees...
                    </TableCell>
                  </TableRow>
                ) : pagedEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No employees found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedEmployees.map((employee) => (
                    <TableRow
                      key={employee.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelect(employee)}
                      data-state={value === String(employee.id) ? 'selected' : undefined}
                    >
                      <TableCell>
                        <Check
                          className={cn(
                            'h-4 w-4',
                            value === String(employee.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Avatar className="h-8 w-8 rounded-md">
                          <AvatarImage src={employee.photo} alt={employee.name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                            {getInitials(employee.name)}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">{employee.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {employee.role || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {employee.department || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {employee.email || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {employee.externalUserId || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination: "Showing X-Y of Z" left, navigation centre, rows per page right */}
          {pagination && (
            <PaginationControls
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              layout="summary"
              itemLabel="employees"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
